import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, LineChart, UtensilsCrossed, Cookie, Pill, Heart, Dumbbell, Timer, TrendingUp, Droplet, Lightbulb, UserCog, Ruler, Clock, BookOpen } from "lucide-react";
import AppShell from "../components/AppShell";
import api from "../lib/api";

const TILES = [
  { to: "/peso-ideal", title: "Mi meta saludable", desc: "Metas progresivas y realistas", icon: Scale, image: "https://customer-assets.emergentagent.com/job_gym-coach-premium/artifacts/vcrwcst5_WhatsApp%20Image%202026-05-30%20at%209.17.39%20AM.jpeg", tone: "gold" },
  { to: "/mi-plan", title: "Mi plan personalizado", desc: "Rangos orientativos + rutinas para ti", icon: Ruler, image: "https://images.pexels.com/photos/4164767/pexels-photo-4164767.jpeg?auto=compress&cs=tinysrgb&w=800", tone: "pink" },
  { to: "/libreta", title: "Mi libreta de entrenamiento", desc: "Registra tus series desde Rutinas y crea aquí tu historial.", icon: Dumbbell, image: "/generated/libreta_hero.png", tone: "gold" },
  { to: "/mi-comida", title: "Registrar mi comida", desc: "Anota lo que comes y controla tus macros del día.", icon: UtensilsCrossed, image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800", tone: "pink" },
  { to: "/progreso", title: "Mi progreso semanal", desc: "Peso + medidas corporales", icon: LineChart, image: "https://images.pexels.com/photos/8033008/pexels-photo-8033008.jpeg?auto=compress&cs=tinysrgb&w=800", tone: "pink" },
  { to: "/consejos", title: "Consejos importantes", desc: "Hábitos para resultados reales", icon: Lightbulb, image: "/generated/tips_hero.jpg", tone: "gold" },
  { to: "/hidratacion", title: "Hidratación", desc: "3 L · horario sugerido", icon: Droplet, image: "https://images.pexels.com/photos/416528/pexels-photo-416528.jpeg?auto=compress&cs=tinysrgb&w=800", tone: "gold" },
  { to: "/comidas", title: "30 comidas saludables", desc: "Desayunos, almuerzos, cenas", icon: UtensilsCrossed, image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=80", tone: "pink" },
  { to: "/snacks", title: "15 snacks saludables", desc: "Altos en proteína", icon: Cookie, image: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=800&q=80", tone: "gold" },
  { to: "/alimentacion-cuba", title: "🇨🇺 Alimentación para Cuba", desc: "Opciones fitness con alimentos locales", icon: UtensilsCrossed, image: "/generated/alimentacion_cuba_hero.jpg", tone: "pink" },
  { to: "/suplementos", title: "Suplementos por edad", desc: "Guía 18-30, 31-45, 46+", icon: Pill, image: "https://images.unsplash.com/photo-1607619056574-7b8d3ee536b2?w=800&q=80", tone: "gold" },
  { to: "/rutinas-mujeres", title: "Rutinas para mujeres", desc: "Por grupo muscular", icon: Heart, image: "https://images.unsplash.com/photo-1646909876562-9a00dab98e65?w=800&q=80", tone: "pink" },
  { to: "/rutinas-hombres", title: "Rutinas para hombres", desc: "Por grupo muscular", icon: Dumbbell, image: "https://images.unsplash.com/photo-1667890787288-ecaaa4bc6994?w=800&q=80", tone: "pink" },
  { to: "/calistenia", title: "Calistenia", desc: "Entrena con tu propio peso corporal.", icon: Dumbbell, image: "/generated/calistenia_hero.png", tone: "gold" },
  { to: "/ayuno", title: "Ayuno 18h + déficit", desc: "Plan adelgazar", icon: Timer, image: "https://images.unsplash.com/photo-1664988935525-e7e0e5e375e3?w=800&q=80", tone: "gold" },
  { to: "/ganar-masa", title: "Ganar masa muscular", desc: "4 comidas + fuerza", icon: TrendingUp, image: "https://customer-assets.emergentagent.com/job_gym-coach-premium/artifacts/isscackq_WhatsApp%20Image%202026-05-30%20at%208.45.35%20AM.jpeg", tone: "pink" },
];

export default function Dashboard() {
  const [profile, setProfile] = useState(null);
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/fitness-plan").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const showReminder = plan && plan.due_for_measurements;
  const hasAnyMeasurement = plan && Object.values(plan.last_known).some((v) => v !== null);

  return (
    <AppShell
      title={profile ? `Hola, ${profile.name}` : "Tu panel"}
      subtitle={profile ? `${profile.sex === "mujer" ? "Bienvenida" : "Bienvenido"}. Tu rango fitness recomendado es ${profile.range_min_kg}–${profile.range_max_kg} kg. 💪 Juntas(os) lo lograremos !!!` : "Bienvenido a tu plan personalizado."}
      showBack={false}
    >
      {profile && (
        <div className="mb-8 -mt-4">
          <Link
            to="/editar-perfil"
            data-testid="edit-profile-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--mep-gold)]/40 bg-[rgba(212,175,55,0.08)] text-sm text-[var(--mep-gold)] hover:bg-[rgba(212,175,55,0.15)] hover:border-[var(--mep-gold)] transition"
          >
            <UserCog size={16} />
            Editar perfil
          </Link>
          <p className="text-xs text-[var(--mep-text-muted)] mt-2">
            ¿Introdujiste algún dato mal? Ajusta estatura, peso, edad, sexo u objetivo cuando quieras.
          </p>
        </div>
      )}

      {showReminder && (
        <Link
          to="/progreso#medidas"
          data-testid="dashboard-measurement-reminder"
          className="block mb-8 p-4 rounded-xl bg-[rgba(232,61,132,0.08)] border border-[var(--mep-pink)]/40 hover:bg-[rgba(232,61,132,0.14)] transition"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[rgba(232,61,132,0.15)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)] flex-shrink-0">
              <Clock size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-eyebrow text-[var(--mep-pink)] mb-0.5">
                {hasAnyMeasurement ? "Toca actualizar tus medidas" : "Registra tus primeras medidas"}
              </div>
              <p className="text-sm text-[var(--mep-text-muted)]">
                {hasAnyMeasurement
                  ? `Han pasado ${plan.days_since_measurements}+ días. Mide cintura, abdomen, cadera, glúteo, muslo y cuádriceps.`
                  : "Añade tus medidas para activar tu plan personalizado basado en tu objetivo."}
              </p>
            </div>
            <div className="text-[var(--mep-pink)] text-sm hidden sm:block">Ir a Progreso →</div>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {TILES.map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.div
              key={t.to}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link to={t.to} data-testid={`tile-${t.to.slice(1)}`} className="mep-tile block group">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={t.image} alt={t.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,10,0.95)] via-[rgba(10,10,10,0.4)] to-transparent" />
                  <div className={`absolute top-4 left-4 w-11 h-11 rounded-full backdrop-blur border flex items-center justify-center ${
                    t.tone === "gold" ? "bg-[rgba(212,175,55,0.15)] border-[var(--mep-gold)]/40 text-[var(--mep-gold)]" : "bg-[rgba(232,61,132,0.15)] border-[var(--mep-pink)]/40 text-[var(--mep-pink)]"
                  }`}>
                    <Icon size={20} />
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-display text-xl md:text-2xl font-light tracking-tight">{t.title}</h3>
                  <p className="text-sm text-[var(--mep-text-muted)] mt-1">{t.desc}</p>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </AppShell>
  );
}
